/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, takeLeading, all, call } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { StoreState } from 'store/store';
import {
  ActionFail,
  ActionRequest,
  ActionSuccess,
  AssetOption,
  AssetOptionType,
  FolderPurchaseOption,
  XError,
} from '@wit/vtv-x-foundation-lib';
import RunOptionsPurchase from './runOptionsPurchase';

export enum ActionType {
  INIT = 'app/folderInfo/init',
  REQUEST = 'app/folderInfo/runOption',
  SUCCESS = 'app/folderInfo/runOption/SUCCESS',
  FAIL = 'app/folderInfo/runOption/FAIL',
}

interface Meta {
  option?: AssetOption;
}

// ACTIONS
type Init = ActionRequest<ActionType.INIT>;
type RunOption = ActionRequest<ActionType.REQUEST, Meta>;
type RunOptionSuccess = ActionSuccess<ActionType.SUCCESS>;
type RunOptionFail = ActionFail<ActionType.FAIL>;

type Actions = Init | RunOption | RunOptionSuccess | RunOptionFail;

const Actions = {
  init: (): Init => ({
    type: ActionType.INIT,
  }),
  request: (option?: AssetOption): RunOption => ({
    type: ActionType.REQUEST,
    meta: { option },
  }),
  success: (): RunOptionSuccess => ({
    type: ActionType.SUCCESS,
  }),
  fail: (error: XError): RunOptionFail => ({
    type: ActionType.FAIL,
    error,
  }),
};

export const Creators = {
  init: Actions.init,
  request: Actions.request,
};

// SELECTORS
export const Selectors = {
  isProcessing: (state: StoreState): boolean => {
    return state.folderInfo.runOption.processing === true;
  },
  isProcessed: (state: StoreState): boolean => {
    return state.folderInfo.runOption.processed === true;
  },
  isSuccess: (state: StoreState): boolean => {
    return state.folderInfo.runOption.processed === true && state.folderInfo.runOption.error === undefined;
  },
  getError: (state: StoreState): XError | undefined => {
    return state.folderInfo.runOption.error;
  },
};

function* requestSaga(action: RunOption): SagaIterator {
  const { option } = action.meta;
  try {
    switch (option?.type) {
      case AssetOptionType.BUNDLE:
      case AssetOptionType.SUBSCRIPTION:
        yield call(RunOptionsPurchase, option as FolderPurchaseOption);
        break;
      default:
        throw XError.fromInternalError(new Error('Unknown option type'));
        break;
    }

    yield put(Actions.success());
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error)));
  }
}

export function* saga(): SagaIterator {
  yield all([takeLeading(ActionType.REQUEST, requestSaga)]);
}

// REDUCER
export interface ReducerState {
  processing: boolean;
  processed: boolean;
  error: XError | undefined;
}

const initialState: ReducerState = {
  processing: false,
  processed: false,
  error: undefined,
};

export default function RunOption(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.INIT: {
      return initialState;
    }
    case ActionType.REQUEST: {
      return {
        ...state,
        processing: true,
        processed: false,
        error: undefined,
      };
    }
    case ActionType.SUCCESS: {
      return {
        ...state,
        processing: false,
        processed: true,
        error: undefined,
      };
    }
    case ActionType.FAIL: {
      return {
        ...state,
        processing: false,
        processed: true,
        error: action.error,
      };
    }
    default:
      return state;
  }
}
